import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listCustomers,
  listCatalog,
  listSubscription,
  createSubscription,
  updateSubscription,
  endSubscription,
  deleteSubscription,
  listOneTimeFee,
  createOneTimeFee,
  cancelOneTimeFee,
  listPrepaidPayments,
  listFlags,
  setFlags,
  listFlagCodes,
  listSettlementHistory,
  generateSettlement,
  toggleTaxInvoice,
  getGoldenCompare,
  setChannelUsed,
  listChannelCards,
  setDefaultChannelCard,
  getPointServiceStatus,
  setPointServiceActive,
} from '../../api/system/system.api';
import { issueCardLink } from '../../api/wizard/initSetting.api';
import { BASE_MONTH_KEY, monthOptions, thisMonth, useBaseMonth } from '../../libs/baseMonth';
import { MAX_BILLING_DATE, MIN_BILLING_DATE, assertBillingMonth, assertBillingStartAt } from '../../libs/billingDate';
import { END_MONTH_PROMPT, isBillableForMonth } from '../../libs/subscriptionPeriod';
import SettlementHistoryTable from './SettlementHistoryTable';
import BillingStartPanel from './BillingStartPanel';
import CardLinkSendPanel from './CardLinkSendPanel';
import ChannelHistoryTimeline from './ChannelHistoryTimeline';

// 고객사 상세페이지(A2 Phase E) — 채널 1개의 정산 관련 설정을 한 화면에서 본다.
//   설정 / 적립 서비스 개통·해지(PERM-06) / 구독(CRUD+이력) / 일회성 비용 / 플래그 / 정산 이력
// 기존 탭들이 "항목 종류별"로 나뉘어 있어 한 고객사를 파악하려면 탭을 4번 옮겨야 했던 것을 합친 화면이다.
// 신규 API 는 정산 이력(listSettlementHistory) 하나뿐이고 나머지는 전부 기존 엔드포인트 재사용.
//
// B.8 — 정산표에서 구독 누락을 발견했을 때 이 화면에서 구독을 추가하고 곧바로 해당 월 정산을 재생성한다.

const won = (n) => (n ?? 0).toLocaleString('ko-KR');

// 서버 billableIn 과 같은 규칙(ENDED 도 종료월까지는 포함) — libs/subscriptionPeriod.js
const isActiveForMonth = isBillableForMonth;


// 적립 서비스 개통 상태 표기(서버 `ChannelServiceStatus.State`).
const POINT_SERVICE_STATE = {
  NONE: { label: '미개통', cls: 'bg-gray-100 text-gray-700' },
  ACTIVE: { label: '개통됨', cls: 'bg-green-100 text-green-800' },
  PARTIAL: { label: '일부 계정 누락', cls: 'bg-yellow-100 text-yellow-800' },
  TERMINATED: { label: '해지됨', cls: 'bg-red-100 text-red-700' },
};

const Section = ({ title, desc, right, children }) => (
  <div className="border rounded mb-4">
    <div className="flex items-center justify-between bg-gray-50 border-b px-4 py-2">
      <div>
        <span className="font-semibold text-gray-800">{title}</span>
        {desc && <span className="ml-2 text-xs text-gray-500">{desc}</span>}
      </div>
      <div className="flex items-center gap-2">{right}</div>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

export default function ChannelDetailAdmin({ channelId: initialChannelId, onChannelChange, onBack }) {
  const [channelId, setChannelId] = useState(initialChannelId ?? '');
  // 🔑 '전체 고객' 목록에서 고른 기준월을 그대로 이어받는다(같은 sessionStorage 키).
  //    종전에는 상세로 들어올 때마다 당월로 초기화돼, 목록에서 지난달을 보다가 상세로 들어오면
  //    **말없이 다른 달 숫자**를 보게 됐다(2026-09-02 수정). libs/baseMonth.js 참고.
  const [month, setMonth] = useBaseMonth(BASE_MONTH_KEY, thisMonth());
  const months = useMemo(() => monthOptions(), []);

  const [customers, setCustomers] = useState([]);
  const [catalog, setCatalog] = useState([]);

  const [subs, setSubs] = useState([]);
  const [fees, setFees] = useState([]);
  const [flags, setFlagList] = useState([]);
  const [flagCodes, setFlagCodes] = useState([]);
  const [history, setHistory] = useState([]);
  // 가입 위저드 토스 선결제(install_payment). 정산서가 아니라 '결제' 라 정산 이력 표와 따로 둔다(2026-10-16).
  const [prepaid, setPrepaid] = useState([]);
  // 결제수단 = 채널이 아니라 Owner 계정 기준. ownerAccountId 가 null 이면 Owner 권한 행 자체가 없다는 뜻이라
  // "카드 없음"과 구분해서 안내해야 한다(둘 다 정산 결제는 실패하지만 조치가 다르다).
  const [cards, setCards] = useState([]);
  const [cardOwner, setCardOwner] = useState(null);
  // 카드 등록 고유링크 발급 결과. `{issued, url, expiresAt, message}` 또는 null(아직 안 만들었다).
  const [cardLink, setCardLink] = useState(null);
  const [cardLinkIssuing, setCardLinkIssuing] = useState(false);
  // 적립(Point) 서비스 개통 현황(PERM-06). 조회 실패는 따로 들고 있는다 — 실패를 '미개통' 으로 그리면
  // 담당자가 이미 쓰는 고객사에 개통을 다시 누르거나 해지된 줄 알고 넘어간다.
  const [pointService, setPointService] = useState(null);
  const [pointServiceErr, setPointServiceErr] = useState('');
  // 이력(타임라인) 다시 읽기 신호(2026-10-19). 이 화면에서 무언가 바꾼 직후 올린다 — 방금 한 일이 바로 보이게.
  const [historyVersion, setHistoryVersion] = useState(0);
  const bumpHistory = () => setHistoryVersion((v) => v + 1);

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => { if (initialChannelId) setChannelId(initialChannelId); }, [initialChannelId]);

  // 채널 목록(기본정보 소스). 활성/비활성 토글 후 화면에 반영하려면 다시 부른다.
  const reloadCustomers = useCallback(
    () => listCustomers().then(({ data }) => setCustomers(data ?? [])).catch(() => {}),
    []
  );

  // 채널 선택 목록·정가표는 채널과 무관하게 1회만.
  useEffect(() => {
    reloadCustomers();
    listCatalog(false).then(({ data }) => setCatalog(data ?? [])).catch(() => {});
    listFlagCodes().then(({ data }) => setFlagCodes(data ?? [])).catch(() => {});
  }, [reloadCustomers]);

  const channel = useMemo(
    () => customers.find((c) => String(c.channelId) === String(channelId)),
    [customers, channelId]
  );

  const reload = useCallback(async () => {
    if (!channelId) return;
    setLoading(true);
    setErr('');
    try {
      const [s, f, fl, h, c, p, pp] = await Promise.all([
        listSubscription(channelId),
        listOneTimeFee(channelId),
        listFlags(channelId),
        listSettlementHistory(channelId),
        // 결제수단 조회가 실패해도 나머지 화면은 살려둔다(정산 작업이 카드 때문에 막히면 안 된다).
        listChannelCards(channelId).catch(() => ({ data: null })),
        // 적립 현황도 같은 이유로 실패를 격리한다 — 대신 사유를 그 구역에 표시한다.
        getPointServiceStatus(channelId)
          .then((r) => ({ data: r?.data ?? null, error: '' }))
          .catch((e) => ({ data: null, error: e?.message ?? '적립 서비스 현황 조회 실패' })),
        // 선결제 이력도 실패를 격리한다 — 정산 이력 본표가 이것 때문에 비면 안 된다.
        listPrepaidPayments(channelId).catch(() => ({ data: [] })),
      ]);
      setSubs(s.data ?? []);
      setFees(f.data ?? []);
      setFlagList(fl.data ?? []);
      setHistory(h.data ?? []);
      setPrepaid(pp?.data ?? []);
      setCards(c.data?.cards ?? []);
      setCardOwner(c.data?.ownerAccountId ?? null);
      setPointService(p.data);
      setPointServiceErr(p.error);
      // 🔴 채널을 바꿨을 때 앞 채널에서 만든 링크가 화면에 남아 있으면, 담당자가 그것을
      //    지금 보고 있는 고객사의 링크로 착각해 **다른 고객에게 보낼 수 있다.**
      setCardLink(null);
      setHistoryVersion((v) => v + 1);
    } catch (e) {
      setErr(e?.message ?? '조회 실패');
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  useEffect(() => { reload(); }, [reload]);

  // 🔴 채널을 바꾸면 앞 채널의 적립 현황을 즉시 지운다. 새 조회가 끝나기 전까지 앞 채널의 버튼이 남아 있으면
  //    **지금 고른 다른 고객사에 해지·개통을 누를 수 있다**(카드 링크를 지우는 것과 같은 이유).
  useEffect(() => { setPointService(null); setPointServiceErr(''); }, [channelId]);

  const run = (fn, okMsg) => async () => {
    setLoading(true);
    setMsg('');
    setErr('');
    try {
      await fn();
      await reload();
      setMsg(okMsg);
    } catch (e) {
      setErr(e?.message ?? '처리 실패');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 카드 등록 **고유링크** 발급(2026-09-10 사용자 지시).
   *
   * 관리자 초기세팅 위저드(`/sysadmin/wizard/init-setting`)에 이미 있던 기능을 **이 화면에도**
   * 붙인 것이다. 왜 = 위저드는 '신규 계정 온보딩' 동선이라, 이미 운영 중인 고객사의 카드가
   * 필요해질 때(만료·교체·정산 결제 실패) 담당자가 갈 곳이 없었다. 정산 실패 원인을
   * 보는 화면(여기)에서 바로 조치할 수 있어야 한다.
   *
   * 🔑 서버 엔드포인트·정책을 그대로 재사용한다(`POST /sysadmin/wizard/init-setting/card-link`).
   *    사업자등록번호가 없으면 서버가 `issued=false` + 안내문구로 **거절**하고, 발급하면
   *    그 계정의 **앞선 링크는 잠긴다**. 판정을 화면에서 다시 하지 않는 이유 =
   *    두 곳에서 판정하면 화면은 발급을 열어 두는데 서버는 거절하는 어긋남이 생긴다.
   *
   * ⚠️ 이 링크로 등록되는 카드가 **기본 결제 카드가 되는지는 상황에 따라 다르다** —
   *    살아 있는 기본카드가 없을 때만 기본이 된다(`PaymentsService.tossCardRegistration`
   *    의 `isDefault = isNew`). 그래서 아래 화면에 그 판정 결과를 문장으로 적어 둔다.
   */
  const createCardLink = async () => {
    if (!cardOwner) return;

    const warn = '카드 등록 고유링크를 새로 만듭니다.\n\n' +
      '· 이 계정에 이전에 보낸 링크가 있으면 그 링크는 즉시 잠깁니다(재사용 불가).\n' +
      '· 링크를 받은 고객은 로그인 없이 사업자등록번호만 확인하면 카드를 등록할 수 있습니다.\n\n' +
      '계속할까요?';
    if (!window.confirm(warn)) return;

    setCardLinkIssuing(true);
    setMsg('');
    setErr('');
    try {
      /*
        🔴 **`issueCardLink` 는 응답 봉투(`CommonResponse`)를 돌려준다** — 실제 결과는 `.data` 다
           (`initSetting.api.js` 가 `res.data` 까지만 벗긴다). 한 겹을 덜 벗기면 `issued` 가
           `undefined` 라 발급이 성공해도 화면은 **항상 실패**로 보인다.
           같은 API 를 쓰는 `InitSettingWizard.createCardLink` 도 `res?.data` 를 읽는다.
      */
      const result = (await issueCardLink(cardOwner))?.data ?? null;
      setCardLink(result);
      if (result?.issued) bumpHistory();
      if (result?.issued) setMsg('카드 등록 고유링크를 발급했습니다.');
      else setErr(result?.message ?? '링크를 만들 수 없습니다.');
    } catch (e) {
      setErr(e?.message ?? '링크 발급 실패');
    } finally {
      setCardLinkIssuing(false);
    }
  };

  const copyCardLink = async (url) => {
    // 🔑 `navigator.clipboard` 는 https 또는 localhost 에서만 있다. 없을 때 조용히 실패하면
    //    담당자는 복사된 줄 알고 빈 값을 붙여 넣는다 — 실패를 눈에 보이게 알린다.
    try {
      await navigator.clipboard.writeText(url);
      setMsg('링크를 복사했습니다.');
    } catch {
      setErr('복사에 실패했습니다. 링크 칸을 클릭해 직접 복사해주세요.');
    }
  };

  /**
   * 기본 결제 카드 변경(2026-09-10 사용자 지시).
   *
   * `[PAYMENT_RISK]` **다음 정산부터 이 카드로 청구된다.** 그래서 확인창에 카드사·번호를 넣어
   * "무엇으로 바뀌는지" 를 되읽게 한다(목록에서 엉뚱한 행을 누르는 것을 막는 유일한 장치다).
   *
   * 🔑 `run()` 을 그대로 쓴다 — 성공하면 `reload()` 가 돌아 표의 '기본' 배지가 옮겨간다.
   *    직접 state 를 고치면 서버가 거절했는데 화면만 바뀌는 상태가 생긴다.
   */
  const changeDefaultCard = (card) => run(async () => {
    const label = `${card.cardCompany ?? '카드'} ${card.maskedCardNumber ?? ''}`.trim();
    const warn = `기본 결제 카드를 ${label} 로 변경합니다.\n\n`
      + '· 다음 정산부터 이 카드로 실제 청구됩니다.\n'
      + '· 고객에게는 별도 안내가 가지 않습니다.\n\n'
      + '계속할까요?';
    // 🔴 취소를 예외로 던지지 않는다 — `run()` 의 catch 가 '처리 실패' 로 표시해 버린다.
    if (!window.confirm(warn)) return;
    await setDefaultChannelCard(Number(channelId), card.id);
  }, '기본 결제 카드를 변경했습니다.');

  /**
   * 살아 있는(삭제되지 않은) 기본 결제 카드가 있는가.
   *
   * 🔴 `isDeleted` 를 함께 보는 것이 중요하다. 서버도 같은 조건으로 판정하는데
   *    (`is_default=1 AND is_deleted=0`), 이 화면이 삭제된 카드의 기본 플래그를 세면
   *    "새 카드가 기본이 되지 않는다" 고 잘못 안내한다.
   */
  const hasLiveDefaultCard = cards.some((c) => c.isDefault && !c.isDeleted);

  // ---------------- 적립 서비스 개통·해지 (PERM-06, 2026-09-23) ----------------
  /**
   * 적립은 `account_permission.service_type='Point'` 행이 있어야 동작한다(2026-09-22~).
   * 종전에는 그 행을 SQL 로만 넣을 수 있어, **신규 적립 고객을 빼먹으면 적립이 조용히 안 됐다.**
   *
   * 🔑 권한 행은 채널의 **모든 계정**(고객 대표 + 직원 + 내부 관리자)에 들어간다 — 판정·대상은 서버가 한다.
   * 🔑 `point_config`(적립율·등급 설정)는 여기서 만들지 않는다. 고객사 채널로 전환해 '포인트 설정'
   *    화면에서 **첫 저장**할 때 적립 진입점 2개와 함께 만들어진다. 여기서는 유무만 보여준다.
   *
   * 🔴 확인창을 `run()` 밖에서 받는다 — `run()` 안에서 취소하면 아무것도 안 했는데 '완료' 가 뜬다.
   */
  const togglePointService = async (active) => {
    const name = channel?.channelName ?? `#${channelId}`;
    const accounts = pointService?.accounts ?? [];
    const missing = accounts.filter((a) => !a.active).length;
    const notConfigured = !pointService?.pointConfigured;

    const warn = !active
      ? `'${name}' 의 적립 서비스를 해지합니다.\n\n`
        + '· 매장 적립 화면과 포인트 API 가 즉시 막힙니다. 영업 중인 매장이면 먼저 고객사와 확인하세요.\n'
        + '· 적립 설정과 고객 포인트 잔액·이력은 지우지 않습니다 — 다시 개통하면 그대로 이어집니다.\n'
        + '· 기존 고객 포인트의 소멸 처리와 소멸 예정 알림톡은 해지와 무관하게 계속 진행됩니다.\n\n'
        + '계속할까요?'
      : pointService?.state === 'PARTIAL'
        ? `'${name}' 에서 적립 권한이 빠진 계정 ${missing}개에 적립 권한을 부여합니다.\n\n계속할까요?`
        : `'${name}' 에 적립 서비스를 개통합니다.\n\n`
          + `· 이 채널에 권한이 있는 계정 ${accounts.length}개(고객 계정 + 내부 관리자)에 적립 권한이 부여됩니다.\n`
          + "· 고객사 화면의 '적립' 메뉴가 바로 동작합니다(이미 열려 있는 화면은 새로고침 후 반영).\n"
          + (notConfigured
            ? "· 적립율·등급 설정은 아직 없습니다 — 고객사 채널로 전환해 '포인트 설정' 화면에서 처음 저장해야 적립이 시작됩니다.\n"
            : '')
          + '\n계속할까요?';
    if (!window.confirm(warn)) return;

    setLoading(true);
    setMsg('');
    setErr('');
    try {
      const { data } = await setPointServiceActive(Number(channelId), active);
      setPointService(data ?? null);
      setPointServiceErr('');
      bumpHistory();
      setMsg(active
        ? `적립 서비스를 개통했습니다 (계정 ${data?.changed ?? 0}개 반영).`
        : `적립 서비스를 해지했습니다 (권한 ${data?.changed ?? 0}건 해지).`);
    } catch (e) {
      setErr(e?.message ?? (active ? '적립 서비스 개통 실패' : '적립 서비스 해지 실패'));
    } finally {
      setLoading(false);
    }
  };

  // ---------------- 구독 ----------------
  // 🔴 `billingStartAt`(첫 유료일)이 빠져 있었다 — 이 화면에서 추가한 구독은 값이 NULL 로 저장돼
  //    채널의 '과금 기준일'(account_channel.billing_start_at)로 폴백했다. 오류가 아니라 **조용히 다른
  //    날짜가 적용**되므로(개시월 일할 계산이 달라진다) 담당자는 알 수 없었다. '전체 고객' 화면에는
  //    처음부터 있던 항목이다. 2026-08-31 수정.
  const emptySub = () => ({
    itemType: '', effectiveFrom: month, branchCount: 1, unitPriceNet: 0, description: '', billingStartAt: '',
  });
  const [subDraft, setSubDraft] = useState(emptySub());

  // 정가표에서 고른 상품의 정가를 단가에 자동 채운다(이후 수정 가능 = 계약 단가는 라인에 독립 탑재).
  const onPickItemType = (itemType) => {
    const c = catalog.find((x) => x.itemType === itemType);
    setSubDraft((d) => ({
      ...d,
      itemType,
      unitPriceNet: c ? c.listPriceNet : d.unitPriceNet,
      branchCount: c?.category === 'ACQUISITION' ? 1 : d.branchCount,
    }));
  };

  /** 지금 고른 상품의 정가표 행. 지점수 비활성 판정에 쓴다. */
  const subDraftCatalog = catalog.find((x) => x.itemType === subDraft.itemType);

  const addSubscription = run(async () => {
    if (!subDraft.itemType) throw new Error('상품(itemType)을 선택해 주세요.');
    // 🔴 2026-09-02 인시던트 방지 — 연도 오타가 0000-00-00 으로 저장되면 이 화면 자체가 열리지 않는다.
    assertBillingMonth(subDraft.effectiveFrom, '적용 시작월');
    assertBillingStartAt(subDraft.billingStartAt);
    const c = catalog.find((x) => x.itemType === subDraft.itemType);
    await createSubscription({
      channelId: Number(channelId),
      itemType: subDraft.itemType,
      category: c?.category ?? 'MONTHLY',
      name: c?.name ?? subDraft.itemType,
      branchCount: Number(subDraft.branchCount) || 1,
      unitPriceNet: Number(subDraft.unitPriceNet) || 0,
      description: subDraft.description || null,
      effectiveFrom: subDraft.effectiveFrom,
      // 비우면 null → 서버가 채널 '과금 기준일'로 폴백한다(의도적으로 비울 수 있어야 한다).
      billingStartAt: subDraft.billingStartAt || null,
    });
    setSubDraft(emptySub());
  }, '구독 추가 완료');

  // B.8 — 구독을 고친 뒤 해당 월 정산 DRAFT 를 다시 만든다(확정분은 서버가 동결 가드로 건너뛴다).
  const regenerate = run(async () => {
    await generateSettlement(channelId, month);
  }, `${month} 정산 재생성 완료`);

  // ---------------- 정산 검증(골든 대조) ----------------
  // 기준월이 바뀌면 이전 결과를 지운다 — 다른 달의 값을 현재 월의 결과로 오독하는 것을 막는다.
  const [golden, setGolden] = useState(null);
  useEffect(() => { setGolden(null); }, [channelId, month]);

  const loadGolden = async () => {
    setLoading(true);
    setErr('');
    setMsg('');
    try {
      const { data } = await getGoldenCompare(channelId, month);
      setGolden(data);
    } catch (e) {
      setErr(e?.message ?? '대조 실패');
    } finally {
      setLoading(false);
    }
  };

  // ---------------- 일회성 비용 ----------------
  const FEE_DEFAULT = { INSTALL: { name: '설치비', amountNet: 50000 }, STICKER: { name: '스티커', amountNet: 30000 } };
  const emptyFee = () => ({ feeType: 'INSTALL', name: '설치비', amountNet: 50000, billingMonth: month, description: '' });
  const [feeDraft, setFeeDraft] = useState(emptyFee());

  const onPickFeeType = (feeType) => {
    const d = FEE_DEFAULT[feeType];
    setFeeDraft((f) => ({ ...f, feeType, ...(d ? { name: d.name, amountNet: d.amountNet } : {}) }));
  };

  const addFee = run(async () => {
    await createOneTimeFee({
      channelId: Number(channelId),
      feeType: feeDraft.feeType,
      name: feeDraft.name,
      amountNet: Number(feeDraft.amountNet) || 0,
      billingMonth: feeDraft.billingMonth,
      description: feeDraft.description || null,
    });
    setFeeDraft(emptyFee());
  }, '일회성 비용 추가 완료');

  // ---------------- 플래그 ----------------
  const [flagInput, setFlagInput] = useState('');
  const currentFlagCodes = useMemo(() => flags.map((f) => f.flagCode ?? f), [flags]);

  const saveFlags = (codes) => run(async () => {
    await setFlags(Number(channelId), codes);
    setFlagInput('');
  }, '플래그 저장 완료');

  const inputCls = 'border rounded px-1.5 py-1 text-sm';

  return (
    <div className="p-6">
      <div className="flex items-center flex-wrap gap-2 mb-4">
        {onBack && (
          <button className="px-2 py-1 text-sm border rounded bg-white" onClick={onBack}>← 전체 고객</button>
        )}
        <h2 className="text-lg font-semibold">고객사 상세</h2>
        <select
          className={inputCls + ' min-w-[280px]'}
          value={channelId}
          onChange={(e) => { setChannelId(e.target.value); onChannelChange?.(e.target.value); }}
        >
          <option value="">채널 선택…</option>
          {customers.map((c) => (
            <option key={c.channelId} value={c.channelId}>
              #{c.channelId} {c.channelName} {c.serviceStatus === 'ACTIVE' ? '' : '(비활성)'}
            </option>
          ))}
        </select>
        <label className="text-sm text-gray-600">기준월
          <select
            className={inputCls + ' ml-1 font-semibold'}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <button className="px-2 py-1 text-sm border rounded" onClick={reload} disabled={!channelId || loading}>새로고침</button>
        {loading && <span className="text-sm text-gray-500">처리 중…</span>}
        {msg && <span className="text-sm text-blue-700">{msg}</span>}
        {err && <span className="text-sm text-red-600">{err}</span>}
      </div>

      {!channelId ? (
        <div className="border rounded bg-gray-50 py-16 text-center text-gray-500">
          채널을 선택하면 적립 서비스·구독·일회성 비용·플래그·정산 이력을 한 화면에서 볼 수 있습니다.
        </div>
      ) : (
        <>
          {/* ---------- 기본 정보 ---------- */}
          <Section title="기본 정보" desc="계정주·연동 상태는 '전체 고객' 탭과 같은 소스">
            {channel ? (
              <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
                <div><span className="text-gray-500">채널 </span>#{channel.channelId} {channel.channelName}</div>
                <div><span className="text-gray-500">계정주 </span>{channel.accountId ?? '—'}</div>
                <div><span className="text-gray-500">서비스 </span>{channel.serviceStatus}</div>
                <BillingStartPanel channelId={Number(channelId)} onChanged={reloadCustomers} />
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={!!channel.isTaxInvoice}
                    onChange={(e) => run(async () => { await toggleTaxInvoice(Number(channelId), e.target.checked); })()}
                  />
                  세금계산서 발행 업체
                </label>
                <button
                  className={`px-2 py-0.5 rounded text-xs border ${channel.isUsed ? 'bg-white text-red-600 border-red-300' : 'bg-white text-green-700 border-green-300'}`}
                  onClick={run(async () => {
                    const next = !channel.isUsed;
                    const warn = next
                      ? `'${channel.channelName}' 서비스를 다시 활성화합니다. 계속할까요?`
                      : `'${channel.channelName}' 서비스를 비활성화합니다.\n\n고객사의 서비스가 실제로 중단됩니다.\n※ 청구는 멈추지 않습니다 — 활성 구독이 남아 있으면 계속 청구되므로, 청구도 멈추려면 구독을 종료해야 합니다.\n\n계속할까요?`;
                    if (!window.confirm(warn)) return;
                    await setChannelUsed(Number(channelId), next);
                    await reloadCustomers();
                  }, '서비스 상태 변경 완료')}
                >
                  {channel.isUsed ? '서비스 비활성화' : '서비스 활성화'}
                </button>
              </div>
            ) : (
              <div className="text-sm text-gray-400">채널 정보를 불러오는 중…</div>
            )}
          </Section>

          {/* ---------- 이력(타임라인, 2026-10-19) ---------- */}
          {/*
            누가·언제·무엇을 했는지(서비스 중단·승인·반려·카드 링크·결제 안내·해지 신청 …)를 한 줄기로 본다.
            종전에는 이 정보가 Slack·DB 여러 표에 흩어져 있었고, 서비스 중단은 언제·누가 했는지 흔적이 아예 없었다.
          */}
          <Section title="이력" desc="channel_history · 최신순 · '복원' = 과거 데이터에서 만든 행(누가 했는지 모름)">
            <ChannelHistoryTimeline key={channelId} channelId={channelId} version={historyVersion} />
          </Section>

          {/* ---------- 적립 서비스 (PERM-06) ---------- */}
          <Section
            title="적립 서비스"
            desc="개통해야 고객사의 적립 화면·포인트 API 가 동작한다 · 권한은 채널의 모든 계정에 함께 들어간다"
            right={pointService && (
              <>
                {pointService.state !== 'ACTIVE' && (
                  <button
                    className="px-2 py-1 text-sm border rounded bg-white text-green-700 border-green-300"
                    disabled={loading || (pointService.accounts ?? []).length === 0}
                    onClick={() => togglePointService(true)}
                  >
                    {pointService.state === 'PARTIAL' ? '빠진 계정에 적용' : '적립 서비스 개통'}
                  </button>
                )}
                {(pointService.state === 'ACTIVE' || pointService.state === 'PARTIAL') && (
                  <button
                    className="px-2 py-1 text-sm border rounded bg-white text-red-600 border-red-300"
                    disabled={loading}
                    onClick={() => togglePointService(false)}
                  >적립 서비스 해지</button>
                )}
              </>
            )}
          >
            {pointServiceErr ? (
              <div className="text-sm text-red-600">
                적립 서비스 현황을 불러오지 못했습니다 — {pointServiceErr}
                <span className="text-gray-500"> (개통 여부를 알 수 없어 버튼을 숨겼습니다. 새로고침해 주세요.)</span>
              </div>
            ) : !pointService ? (
              <div className="text-sm text-gray-400">불러오는 중…</div>
            ) : (
              <div className="text-sm">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-2">
                  <div>
                    <span className="text-gray-500">상태 </span>
                    <span className={`px-2 py-0.5 rounded text-xs ${POINT_SERVICE_STATE[pointService.state]?.cls ?? 'bg-gray-100 text-gray-700'}`}>
                      {POINT_SERVICE_STATE[pointService.state]?.label ?? pointService.state}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">적립 설정 </span>
                    {pointService.pointConfigured ? '완료' : <span className="text-gray-700">없음</span>}
                  </div>
                </div>
                {(pointService.state === 'ACTIVE' || pointService.state === 'PARTIAL') && !pointService.pointConfigured && (
                  <div className="mb-2 px-3 py-2 rounded bg-yellow-50 text-yellow-800">
                    권한은 열렸지만 적립율·등급 설정이 없어 아직 적립이 되지 않습니다.
                    고객사 채널로 전환해 '포인트 설정' 화면에서 처음 저장하면 설정과 적립 진입점이 함께 만들어집니다.
                  </div>
                )}
                {pointService.state === 'PARTIAL' && (
                  <div className="mb-2 px-3 py-2 rounded bg-yellow-50 text-yellow-800">
                    개통 뒤에 이 채널에 붙은 계정은 적립 권한이 없습니다. 그 계정으로는 적립 화면이 막힙니다 — '빠진 계정에 적용' 을 눌러 주세요.
                  </div>
                )}
                {(pointService.accounts ?? []).length === 0 ? (
                  <div className="text-gray-500">이 채널에 권한을 가진 계정이 없어 개통할 수 없습니다. 계정 연결(위저드·승인)을 먼저 마쳐 주세요.</div>
                ) : (
                  <table className="text-sm border">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-3 py-1 text-left font-normal">계정</th>
                        <th className="px-3 py-1 text-left font-normal">이 채널의 다른 권한</th>
                        <th className="px-3 py-1 text-left font-normal">적립 권한</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pointService.accounts.map((a) => (
                        <tr key={a.accountId} className="border-t">
                          <td className="px-3 py-1">{a.accountId}{String(a.accountId) === String(channel?.accountId) && <span className="ml-1 text-xs text-gray-500">(계정주)</span>}</td>
                          <td className="px-3 py-1 text-gray-600">{(a.otherServiceTypes ?? []).join(', ') || '—'}</td>
                          <td className={`px-3 py-1 ${a.active ? 'text-green-700' : 'text-gray-500'}`}>{a.serviceGrant ?? '없음'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </Section>

          {/* ---------- 결제 수단 ---------- */}
          {/*
            정산 '결제 실행'이 실패했을 때 담당자가 DB 를 열지 않고 원인을 좁히기 위한 화면이다.
            기본 카드가 없으면 결제 자체가 시작되지 않고(SettlementPaymentService.chargeCard),
            있으면 카드사 승인 거절이므로 정산관리 목록의 '결제 실패' 사유를 봐야 한다.
          */}
          <Section
            title="결제 수단"
            desc={cardOwner ? `계정 ${cardOwner} 에 등록된 카드 (채널이 아니라 계정 단위)` : '계정주(Owner) 미지정'}
            right={
              <button
                onClick={createCardLink}
                disabled={!cardOwner || cardLinkIssuing}
                title={cardOwner
                  ? '고객이 로그인 없이 사업자등록번호만 확인하고 카드를 등록할 수 있는 1회용 링크를 만든다'
                  : 'Owner 계정이 없어 발급할 수 없다'}
                className="px-2 py-0.5 rounded text-xs border bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200"
              >
                {cardLinkIssuing ? '발급 중…' : '카드 등록 링크 만들기'}
              </button>
            }
          >
            {!cardOwner ? (
              <div className="text-sm text-red-600">
                이 채널에 Owner 권한 계정이 없어 결제수단을 조회할 수 없습니다. 정산 결제도 실행되지 않습니다.
                <br />
                카드 등록 링크도 이 상태에서는 발급할 수 없습니다 — 먼저 Owner 권한 계정을 지정해주세요.
              </div>
            ) : (
              <>
                {/* ---------- 카드 등록 링크 LMS 보내기(2026-10-19) ---------- */}
                <CardLinkSendPanel
                  key={channelId}
                  channelId={channelId}
                  onSent={bumpHistory}
                  onMessage={(m) => { setErr(''); setMsg(m); }}
                  onError={(m) => { setMsg(''); setErr(m); }}
                />

                {/* ---------- 카드 등록 고유링크 발급 결과 ---------- */}
                {cardLink && (
                  <div className={`mb-3 rounded border p-3 text-xs ${
                    cardLink.issued ? 'bg-indigo-50 border-indigo-200' : 'bg-amber-50 border-amber-200'}`}>
                    {cardLink.issued ? (
                      <>
                        <div className="text-gray-500 mb-1">
                          고객에게 보낼 링크 · <b>{cardLink.expiresAt}</b> 까지 유효
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            readOnly
                            value={cardLink.url}
                            onFocus={(e) => e.target.select()}
                            className="flex-1 min-w-0 px-2 py-1.5 bg-white border border-gray-200 rounded font-mono text-[11px] text-gray-700"
                          />
                          <button
                            onClick={() => copyCardLink(cardLink.url)}
                            className="shrink-0 px-2 py-1.5 rounded border bg-white border-gray-300 hover:bg-gray-50 font-semibold text-gray-600"
                          >
                            복사
                          </button>
                        </div>
                        {/*
                          🔴 **여기서 기본카드 여부를 반드시 말해 준다.** 담당자가 이 링크를 보내는 이유는
                             대개 "청구가 실패하니 카드를 새로 받자" 인데, 살아 있는 기본카드가 이미 있으면
                             새 카드는 **추가 카드로만 붙고 청구는 옛 카드로 계속 나간다**
                             (`PaymentsService.tossCardRegistration` 의 `isDefault = isNew`).
                             이 문장이 없으면 "카드를 새로 받았는데 왜 또 실패하냐" 가 반복된다.
                        */}
                        <div className={`mt-2 leading-relaxed ${hasLiveDefaultCard ? 'text-red-600' : 'text-gray-500'}`}>
                          {hasLiveDefaultCard ? (
                            <>
                              ⚠️ 이 계정에는 <b>이미 기본 결제 카드가 있습니다</b>. 고객이 링크에서
                              <b> ‘기본 결제 카드로 설정’ 에 ‘네’</b> 를 고르면 새 카드로 청구 카드가 바뀌고,
                              <b> ‘아니오’ 면 추가 카드로만 등록</b>되어 청구는 계속 기존 카드로 나갑니다.
                              담당자가 직접 바꾸려면 <b>아래 표의 ‘기본으로 지정’</b> 을 누르세요.
                            </>
                          ) : (
                            <>
                              이 계정에는 살아 있는 기본 결제 카드가 없으므로, 고객이 무엇을 고르든
                              이 링크로 등록되는 카드가 <b>그대로 기본 결제 카드가 됩니다</b>
                              (청구할 카드가 한 장은 있어야 합니다).
                            </>
                          )}
                        </div>
                        <div className="mt-2 text-gray-400 leading-relaxed">
                          · 이 링크로 가능한 일은 <b>카드 등록 하나</b>입니다(조회·삭제·기본카드 변경 불가).<br />
                          · 사업자등록번호를 <b>5회</b> 틀리면 링크가 잠깁니다.<br />
                          · 새로 발급하면 이전에 보낸 링크는 즉시 잠깁니다.
                        </div>
                      </>
                    ) : (
                      <div className="text-amber-700 leading-relaxed">
                        {cardLink.message ?? '링크를 만들 수 없습니다.'}
                      </div>
                    )}
                  </div>
                )}

                <table className="min-w-full text-sm border">
                  <thead className="bg-gray-50 text-xs">
                    <tr className="divide-x">
                      <th className="px-2 py-1 text-left">카드사</th>
                      <th className="px-2 py-1 text-left">카드번호</th>
                      <th className="px-2 py-1">구분</th>
                      <th className="px-2 py-1">등록일시</th>
                      <th className="px-2 py-1">기본 결제 카드</th>
                      <th className="px-2 py-1">상태</th>
                      <th className="px-2 py-1">삭제일시</th>
                      <th className="px-2 py-1">기본 지정</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cards.length === 0 && (
                      <tr>
                        <td className="px-2 py-3 text-center text-gray-400" colSpan={8}>
                          등록된 카드가 없습니다. 이 상태에서는 정산 '결제 실행'이 카드 미등록으로 실패합니다.
                        </td>
                      </tr>
                    )}
                    {cards.map((c) => (
                      <tr key={c.id} className={`border-b divide-x ${c.isDeleted ? 'text-gray-400 line-through' : ''}`}>
                        <td className="px-2 py-1">{c.cardCompany ?? '—'}</td>
                        <td className="px-2 py-1 font-mono">{c.maskedCardNumber ?? '—'}</td>
                        <td className="px-2 py-1 text-center">{c.cardType ? `${c.cardType}카드` : '—'}</td>
                        <td className="px-2 py-1 text-center whitespace-nowrap">{c.registeredAt ?? '—'}</td>
                        <td className="px-2 py-1 text-center">
                          {c.isDefault ? (
                            <span className="px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-800">기본</span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-2 py-1 text-center">
                          {c.isDeleted ? (
                            <span className="px-1.5 py-0.5 rounded text-xs bg-gray-200 text-gray-500">삭제됨</span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700">사용</span>
                          )}
                        </td>
                        {/* 삭제 시각이 비어 있으면 deleted_at 컬럼 도입(2026-08-06) 전에 지워진 행이다.
                            소급 추정하지 않으므로 '시각 미상'으로 구분해 표시한다. */}
                        <td className="px-2 py-1 text-center whitespace-nowrap">
                          {!c.isDeleted
                            ? '—'
                            : c.deletedAt ?? <span className="text-gray-400">시각 미상</span>}
                        </td>
                        {/*
                          기본 결제 카드 변경(2026-09-10).
                          🔴 **삭제된 카드에는 버튼을 두지 않는다.** 삭제된 카드에 기본 플래그가 서면
                             결제 조회(`is_deleted=false`)에 걸리지 않아 "기본카드가 있는데 없는 것과
                             같은" 상태가 된다(서버도 거절하지만, 누를 수 있게 두면 안 된다).
                        */}
                        <td className="px-2 py-1 text-center">
                          {c.isDefault ? (
                            <span className="text-xs text-gray-400">현재 기본</span>
                          ) : c.isDeleted ? (
                            <span className="text-xs text-gray-300">—</span>
                          ) : (
                            <button
                              onClick={changeDefaultCard(c)}
                              disabled={loading}
                              className="px-2 py-0.5 rounded text-xs border bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200"
                              title="이 카드를 기본 결제 카드로 지정한다 (다음 정산부터 이 카드로 청구된다)"
                            >
                              기본으로 지정
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-2 text-xs text-gray-500 leading-relaxed">
                  · 정산·수동 결제는 <b>기본 결제 카드</b> 한 장으로만 청구됩니다. 기본 카드가 없으면 결제가 실행되지 않습니다.<br />
                  · 카드번호는 토스가 마스킹한 값을 그대로 보관한 것이라, <b>끝자리가 <code>*</code> 로 가려져 있을 수 있습니다</b>(원본 번호는 저장하지 않습니다).<br />
                  · 등록일시는 토스 인증 시각(<code>authenticated_at</code>) 기준 KST 입니다.<br />
                  · 삭제된 카드도 결제 이력 검증을 위해 취소선 + 삭제일시와 함께 남깁니다(DB 에서 실제로 지우지 않습니다).<br />
                  · 고객은 <b>마지막 남은 1장은 삭제할 수 없습니다</b>. 기본 카드를 지우면 남은 카드 중 최근 등록분이 자동으로 기본이 됩니다.<br />
                  · <b>새로 등록되는 카드</b>가 기본이 되는 경우는 둘입니다 — <b>살아 있는 기본 카드가 없을 때</b>,
                  또는 <b>카드 등록 링크에서 고객이 ‘기본으로 설정’ 에 ‘네’ 를 고른 때</b>(2026-09-10부터).
                  그 밖에는 추가 카드로만 붙고 청구 카드는 바뀌지 않습니다.<br />
                  · <b>기본 지정</b> 열의 버튼으로 담당자가 직접 바꿀 수 있습니다.
                  <b>다음 정산부터 그 카드로 청구</b>되고 고객에게 별도 안내는 가지 않습니다.
                </div>
              </>
            )}
          </Section>

          {/* ---------- 구독 ---------- */}
          <Section
            title="구독 (계약대장)"
            desc="가격은 라인에 직접 탑재된다 — 정가표를 나중에 바꿔도 기존 계약가는 안 바뀐다"
            right={
              <button className="px-2 py-1 text-sm border rounded bg-white" onClick={regenerate} disabled={loading}>
                {month} 정산 재생성
              </button>
            }
          >
            <table className="min-w-full text-sm border mb-3">
              <thead className="bg-gray-50 text-xs">
                <tr className="divide-x">
                  <th className="px-2 py-1 text-left">상품</th>
                  <th className="px-2 py-1 text-right">지점수</th>
                  <th className="px-2 py-1 text-right">단가(net)</th>
                  <th className="px-2 py-1 text-right">월 공급가</th>
                  <th className="px-2 py-1">적용 기간</th>
                  <th className="px-2 py-1">첫 유료일</th>
                  <th className="px-2 py-1">설명</th>
                  <th className="px-2 py-1">상태</th>
                  <th className="px-2 py-1">작업</th>
                </tr>
              </thead>
              <tbody>
                {subs.length === 0 && (
                  <tr><td className="px-2 py-3 text-center text-gray-400" colSpan={9}>등록된 구독이 없습니다. 활성 서비스인데 비어 있다면 청구가 누락된다.</td></tr>
                )}
                {subs.map((s) => {
                  const active = isActiveForMonth(s, month);
                  return (
                    <tr key={s.id} className={`border-b divide-x ${active ? '' : 'text-gray-400'}`}>
                      <td className="px-2 py-1">{s.name} <span className="text-xs text-gray-400">{s.itemType}</span></td>
                      <td className="px-2 py-1 text-right">{s.branchCount}</td>
                      <td className="px-2 py-1 text-right">{won(s.unitPriceNet)}</td>
                      <td className="px-2 py-1 text-right">{won(s.branchCount * s.unitPriceNet)}</td>
                      <td className="px-2 py-1">{s.effectiveFrom} ~ {s.effectiveTo ?? ''}</td>
                      {/* 비어 있으면 채널 '과금 기준일'로 폴백한다는 사실을 셀에서 바로 알 수 있게 적는다.
                          종전에는 이 컬럼이 아예 없어서 무엇이 저장됐는지 확인할 방법이 없었다. */}
                      <td className="px-2 py-1 whitespace-nowrap">
                        {s.billingStartAt
                          ? String(s.billingStartAt).slice(0, 10)
                          : <span className="text-xs text-gray-400" title="구독에 첫 유료일이 없어 채널의 '과금 시작일'이 적용됩니다.">채널 과금 시작일</span>}
                      </td>
                      <td className="px-2 py-1">{s.description ?? ''}</td>
                      <td className="px-2 py-1">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                          {/* ENDED 라도 종료월에는 청구된다 — '활성' 과 구분해 보여 준다(libs/subscriptionPeriod.js). */}
                          {active ? (s.status === 'ENDED' ? `${month} 청구(종료월)` : `${month} 활성`) : s.status}
                        </span>
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap">
                        <button
                          className="text-xs underline mr-2"
                          onClick={run(async () => {
                            const to = window.prompt(END_MONTH_PROMPT, s.effectiveTo || month);
                            if (!to) return;
                            assertBillingMonth(to.trim(), '종료월');
                            await endSubscription(s.id, to.trim());
                          }, '구독 종료 완료')}
                        >종료</button>
                        <button
                          className="text-xs underline mr-2"
                          onClick={run(async () => {
                            const p = window.prompt('단가(net)', s.unitPriceNet);
                            if (p === null) return;
                            await updateSubscription(s.id, { ...s, unitPriceNet: Number(p) || 0 });
                          }, '구독 수정 완료')}
                        >단가수정</button>
                        <button
                          className="text-xs underline mr-2"
                          onClick={run(async () => {
                            const cur = s.billingStartAt ? String(s.billingStartAt).slice(0, 10) : '';
                            const v = window.prompt(
                              '첫 유료일(YYYY-MM-DD)\n\n개시월은 이 날짜부터 일할 계산됩니다.\n비우면 채널의 과금 시작일이 적용됩니다.',
                              cur,
                            );
                            if (v === null) return;
                            const next = v.trim();
                            // ⚠️ 서버 update 는 null 을 '변경 없음'으로 본다(`req.billingStartAt?.let`) →
                            //    빈 값으로 지우는 것은 이 경로로 불가능하다. 오해하지 않게 이유를 말한다.
                            if (next === '') throw new Error("첫 유료일은 이 버튼으로 지울 수 없습니다. 구독을 삭제하고 다시 추가해 주세요.");
                            if (!/^\d{4}-\d{2}-\d{2}$/.test(next)) throw new Error('날짜 형식이 올바르지 않습니다(YYYY-MM-DD).');
                            await updateSubscription(s.id, { ...s, billingStartAt: next });
                          }, '첫 유료일 수정 완료')}
                        >첫유료일수정</button>
                        <button
                          className="text-xs underline text-red-600"
                          onClick={run(async () => {
                            if (!window.confirm(`구독 #${s.id} (${s.name})을(를) 완전히 삭제합니다. 되돌릴 수 없습니다.`)) return;
                            await deleteSubscription(s.id);
                          }, '구독 삭제 완료')}
                        >삭제</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="flex flex-wrap items-end gap-2 text-sm">
              <label>상품
                <select className={inputCls + ' ml-1'} value={subDraft.itemType} onChange={(e) => onPickItemType(e.target.value)}>
                  <option value="">선택…</option>
                  {catalog
                    .filter((c) => c.category === 'MONTHLY' || c.category === 'ACQUISITION')
                    .map((c) => <option key={c.itemType} value={c.itemType}>{c.name} ({won(c.listPriceNet)})</option>)}
                </select>
              </label>
              {/* 획득 과금(ACQUISITION)은 지점 개념이 없다(항상 1) — '전체 고객' 화면과 같은 규칙. */}
              <label>지점수
                <input
                  type="number"
                  className={inputCls + ' w-16'}
                  value={subDraft.branchCount}
                  onChange={(e) => setSubDraft({ ...subDraft, branchCount: e.target.value })}
                  disabled={subDraftCatalog?.category === 'ACQUISITION'}
                />
              </label>
              <label>단가(net)
                <input type="number" className={inputCls + ' w-24'} value={subDraft.unitPriceNet} onChange={(e) => setSubDraft({ ...subDraft, unitPriceNet: e.target.value })} />
              </label>
              <label>적용 시작월(effectiveFrom)
                <input className={inputCls + ' w-24'} value={subDraft.effectiveFrom} onChange={(e) => setSubDraft({ ...subDraft, effectiveFrom: e.target.value })} placeholder="2026-07" />
              </label>
              {/* 🔴 종전에 없던 항목. 이게 없으면 값이 NULL 로 저장돼 채널 '과금 기준일'로 조용히 폴백한다. */}
              <label>첫 유료일(billingStartAt)
                <input type="date" min={MIN_BILLING_DATE} max={MAX_BILLING_DATE} className={inputCls + ' w-36'} value={subDraft.billingStartAt} onChange={(e) => setSubDraft({ ...subDraft, billingStartAt: e.target.value })} />
              </label>
              <label>설명 <input className={inputCls + ' w-48'} value={subDraft.description} onChange={(e) => setSubDraft({ ...subDraft, description: e.target.value })} placeholder="예: 3개 지점" /></label>
              <button className="px-2 py-1 border rounded bg-white" onClick={addSubscription} disabled={loading}>구독 추가</button>
            </div>
            <p className="mt-2 text-xs text-gray-500 leading-relaxed">
              <b>적용 시작월</b>(YYYY-MM) = 이 구독이 정산에 잡히기 시작하는 <b>달</b>. 이 달보다 이전 달 정산에는 잡히지 않습니다.<br />
              <b>첫 유료일</b>(YYYY-MM-DD) = 실제 과금이 시작되는 <b>날짜</b>. 그 날짜가 든 달은 <b>남은 일수만큼 일할 계산</b>되고,
              그 이전 달은 과금 0(무료체험)입니다. <b>비우면 채널의 '과금 시작일'(고객사 상세 '과금 시작' 버튼으로 고정)이 대신 적용됩니다.</b>
            </p>
          </Section>

          {/* ---------- 일회성 비용 ---------- */}
          <Section title="일회성 비용" desc="PENDING → 해당 월 정산에 방출 → 확정 시 BILLED · PREPAID = 가입 위저드 토스 선결제(자동 생성, 정산에 미방출)">
            <table className="min-w-full text-sm border mb-3">
              <thead className="bg-gray-50 text-xs">
                <tr className="divide-x">
                  <th className="px-2 py-1 text-left">종류</th>
                  <th className="px-2 py-1 text-left">항목명</th>
                  <th className="px-2 py-1 text-right">금액(net)</th>
                  <th className="px-2 py-1">청구월</th>
                  <th className="px-2 py-1">상태</th>
                  <th className="px-2 py-1">작업</th>
                </tr>
              </thead>
              <tbody>
                {fees.length === 0 && <tr><td className="px-2 py-3 text-center text-gray-400" colSpan={6}>등록된 일회성 비용이 없습니다.</td></tr>}
                {fees.map((f) => (
                  <tr key={f.id} className="border-b divide-x">
                    <td className="px-2 py-1">{f.feeType}</td>
                    <td className="px-2 py-1">{f.name}</td>
                    <td className="px-2 py-1 text-right">{won(f.amountNet)}</td>
                    <td className="px-2 py-1">{f.billingMonth}</td>
                    <td className="px-2 py-1">{f.status}</td>
                    <td className="px-2 py-1">
                      {f.status === 'PENDING' ? (
                        <button className="text-xs underline text-red-600" onClick={run(async () => { await cancelOneTimeFee(f.id); }, '취소 완료')}>취소</button>
                      ) : (
                        <span className="text-xs text-gray-400">정산 재오픈 후 정정</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex flex-wrap items-end gap-2 text-sm">
              <label>종류
                <select className={inputCls + ' ml-1'} value={feeDraft.feeType} onChange={(e) => onPickFeeType(e.target.value)}>
                  <option value="INSTALL">설치비</option>
                  <option value="STICKER">스티커</option>
                  <option value="ETC">기타</option>
                </select>
              </label>
              <label>항목명 <input className={inputCls + ' w-32'} value={feeDraft.name} onChange={(e) => setFeeDraft({ ...feeDraft, name: e.target.value })} /></label>
              <label>금액(net) <input className={inputCls + ' w-24'} value={feeDraft.amountNet} onChange={(e) => setFeeDraft({ ...feeDraft, amountNet: e.target.value })} /></label>
              <label>청구월 <input className={inputCls + ' w-24'} value={feeDraft.billingMonth} onChange={(e) => setFeeDraft({ ...feeDraft, billingMonth: e.target.value })} /></label>
              <button className="px-2 py-1 border rounded bg-white" onClick={addFee} disabled={loading}>추가</button>
            </div>
          </Section>

          {/* ---------- 플래그 ---------- */}
          <Section title="플래그" desc="저장 시 통째 교체 · 수익 대시보드 필터로 쓰인다">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {currentFlagCodes.length === 0 && <span className="text-sm text-gray-400">지정된 플래그가 없습니다.</span>}
              {currentFlagCodes.map((code) => (
                <span key={code} className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 text-sm">
                  {code}
                  <button
                    className="ml-1 text-indigo-500"
                    onClick={saveFlags(currentFlagCodes.filter((c) => c !== code))}
                  >×</button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <input
                className={inputCls + ' w-40'}
                list="channel-flag-codes"
                value={flagInput}
                onChange={(e) => setFlagInput(e.target.value)}
                placeholder="플래그 입력/선택"
              />
              <datalist id="channel-flag-codes">
                {flagCodes.map((c) => <option key={c} value={c} />)}
              </datalist>
              <button
                className="px-2 py-1 border rounded bg-white"
                disabled={!flagInput.trim() || loading}
                onClick={saveFlags([...new Set([...currentFlagCodes, flagInput.trim()])])}
              >추가</button>
            </div>
          </Section>

          {/* ---------- 정산 검증(골든 대조) ---------- */}
          <Section
            title="정산 검증 (legacy ↔ 신 정산 대조)"
            desc="읽기 전용 — 정산서를 생성하지 않는다"
            right={
              <button className="px-2 py-1 text-sm border rounded bg-white" onClick={loadGolden} disabled={loading}>
                {month} 대조 실행
              </button>
            }
          >
            {!golden ? (
              <div className="text-sm text-gray-500">
                기준월을 정하고 <b>대조 실행</b>을 누르면 기존 청구 방식(<code>getBillInfo</code>)과 신 정산 계산을
                나란히 비교합니다. 배포 전 금액 검증(GATE-2)에 사용합니다.
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm mb-3">
                  <div><span className="text-gray-500">legacy 총액 </span>
                    {golden.legacyTotal == null ? <span className="text-red-600">계산 불가</span> : <b>{won(golden.legacyTotal)}</b>}
                  </div>
                  <div><span className="text-gray-500">신 정산 총액 </span><b>{won(golden.newTotal)}</b></div>
                  <div>
                    <span className="text-gray-500">차이 </span>
                    {golden.delta == null ? '—' : (
                      <b className={golden.delta === 0 ? 'text-green-700' : Math.abs(golden.delta) > 10000 ? 'text-red-600' : 'text-amber-600'}>
                        {golden.delta > 0 ? '+' : ''}{won(golden.delta)}
                      </b>
                    )}
                  </div>
                  <div className="text-gray-500">공급가 {won(golden.newSupplyTotal)} · 과세 {won(golden.newTaxableTotal)} · 부가세 {won(golden.newVat)}</div>
                </div>

                {golden.legacyError && (
                  <div className="mb-3 text-xs text-red-600">legacy 계산 실패: {golden.legacyError} (채널 설정 결측일 수 있습니다)</div>
                )}

                <div className="text-xs text-gray-500 mb-3">
                  차이가 <b>작은 양수</b>면 정상입니다 — 신 방식은 부가세를 공급가 합계에 한 번만 절사하고,
                  legacy는 항목마다 부가세 포함 단가를 곱해 절사 오차가 누적됩니다.
                  <b> 음수</b>면 대개 개시월 일할(legacy는 일할을 안 함)입니다.
                  <b className="text-red-600"> 차이가 크면</b> 구독 미등록·상품(itemType) 오지정·획득과금 구독 유무를 먼저 확인하세요.
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium mb-1">기존 방식 (부가세 포함 단가)</div>
                    <table className="min-w-full text-xs border">
                      <thead className="bg-gray-50"><tr className="divide-x">
                        <th className="px-2 py-1 text-left">구분</th><th className="px-2 py-1 text-left">항목</th>
                        <th className="px-2 py-1 text-right">수량</th><th className="px-2 py-1 text-right">금액</th>
                      </tr></thead>
                      <tbody>
                        {golden.legacyGroups.length === 0 && <tr><td className="px-2 py-2 text-center text-gray-400" colSpan={4}>없음</td></tr>}
                        {golden.legacyGroups.flatMap((g) =>
                          g.items.map((it, i) => (
                            <tr key={`${g.type}-${i}`} className="border-b divide-x">
                              {i === 0 && <td className="px-2 py-1 align-top" rowSpan={g.items.length}>{g.type}</td>}
                              <td className="px-2 py-1">{it.name}</td>
                              <td className="px-2 py-1 text-right">{Number(it.count).toLocaleString()}</td>
                              <td className="px-2 py-1 text-right">{won(it.price)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div>
                    <div className="text-sm font-medium mb-1">신 정산 (부가세 별도 공급가)</div>
                    <table className="min-w-full text-xs border">
                      <thead className="bg-gray-50"><tr className="divide-x">
                        <th className="px-2 py-1 text-left">구분</th><th className="px-2 py-1 text-left">항목</th>
                        <th className="px-2 py-1 text-right">수량</th><th className="px-2 py-1 text-right">공급가</th>
                      </tr></thead>
                      <tbody>
                        {golden.newItems.length === 0 && (
                          <tr><td className="px-2 py-2 text-center text-red-600" colSpan={4}>
                            항목 0건 — 구독이 등록되지 않았을 가능성이 큽니다.
                          </td></tr>
                        )}
                        {golden.newItems.map((it, i) => (
                          <tr key={i} className="border-b divide-x">
                            <td className="px-2 py-1">{it.category}</td>
                            <td className="px-2 py-1">{it.name}{it.vatExempt && <span className="text-gray-400"> (면세)</span>}</td>
                            <td className="px-2 py-1 text-right">{Number(it.quantity).toLocaleString()}</td>
                            <td className="px-2 py-1 text-right">{won(it.supplyAmountNet)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </Section>

          {/* ---------- 정산 이력 ---------- */}
          {/* 🔑 '정산서 확정' 과 '결제(수납)' 는 다른 단계다(2026-10-01). 종전 화면은 상태 배지 + '확정일' 만 있어
              CONFIRMED 를 결제 완료로 오해하기 쉬웠고, 실제 결제 여부(paid_at)는 어디에도 보이지 않았다.
              → 정산서 칸(상태 + 확정 시각)과 결제 칸(완료/미결제/실패)을 나누고, 행을 누르면 그 정산서 항목을 펼친다. */}
          <Section
            title="정산 이력"
            desc="정산서 확정 = 금액 동결 + 고객 '월 별 내역서' 노출(결제와 별개) · 결제 = 카드 결제 성공 또는 입금 확인 · 행을 누르면 내역"
          >
            {/* 표·펼치기·결제 칸 규칙은 정산관리 화면과 공유한다 — SettlementHistoryTable.js */}
            <SettlementHistoryTable channelId={channelId} history={history} />
            <p className="mt-2 text-xs text-gray-500">
              항목 편집·확정·재오픈·결제는 <b>정산 관리(신)</b> 탭에서 합니다. 이 화면은 조회 + 구독/비용 정정 후 재생성까지만 담당합니다.
            </p>

            {/* 🔑 선결제는 정산서 없이 카드 승인만 있는 '결제' 다(2026-10-16). 월 정산표에 끼우면 청구월·확정 칸이 비어 어색하고,
                PREPAID 일회성 비용 행만 보면 실제 승인액(VAT 포함)·실패 시도가 안 보인다 → 원본 install_payment 를 따로 표로 둔다. */}
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-gray-800">선결제 이력 (가입 위저드 토스 결제)</span>
                <span className="text-xs text-gray-500">원본 install_payment · 월 정산서와 별개 · 성공 건은 일회성 비용에 PREPAID 로 반영</span>
              </div>
              <table className="min-w-full text-sm border">
                <thead className="bg-gray-50 text-xs">
                  <tr className="divide-x">
                    <th className="px-2 py-1 text-left">결제일시</th>
                    <th className="px-2 py-1 text-left">상품명</th>
                    <th className="px-2 py-1 text-right">설치비(net)</th>
                    <th className="px-2 py-1 text-right">스티커(net)</th>
                    <th className="px-2 py-1 text-right">VAT</th>
                    <th className="px-2 py-1 text-right">승인액</th>
                    <th className="px-2 py-1">상태</th>
                    <th className="px-2 py-1">일회성 비용 반영</th>
                  </tr>
                </thead>
                <tbody>
                  {prepaid.length === 0 && (
                    <tr><td className="px-2 py-3 text-center text-gray-400" colSpan={8}>선결제 내역이 없습니다.</td></tr>
                  )}
                  {prepaid.map((p) => (
                    <tr key={p.id} className="border-b divide-x">
                      <td className="px-2 py-1 whitespace-nowrap">{p.paidAt ?? p.requestedAt}</td>
                      <td className="px-2 py-1" title={`orderId ${p.orderId} · 계정 ${p.accountId}`}>{p.orderName}</td>
                      <td className="px-2 py-1 text-right">{won(p.setupFeeNet)}</td>
                      <td className="px-2 py-1 text-right">{p.stickerFeeNet > 0 ? `${won(p.stickerFeeNet)} (${p.stickerQuantity}장)` : '-'}</td>
                      <td className="px-2 py-1 text-right text-gray-500">{won(p.vat)}</td>
                      <td className="px-2 py-1 text-right font-semibold">{won(p.amount)}</td>
                      <td className="px-2 py-1 text-center">
                        {p.status === 'PAID' && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-xs">결제 완료</span>}
                        {p.status === 'FAILED' && (
                          <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-xs" title={p.failMessage ?? ''}>실패</span>
                        )}
                        {p.status === 'REQUESTED' && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-xs">진행 중</span>}
                        {p.status === 'CANCELED' && (
                          <span className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 text-xs" title="토스 콘솔에서 취소된 결제. 매출 집계에서 제외됩니다.">취소</span>
                        )}
                      </td>
                      <td className="px-2 py-1 text-center text-xs">
                        {p.status !== 'PAID' ? (
                          <span className="text-gray-400">-</span>
                        ) : p.oneTimeFeeAttached ? (
                          <span className="text-blue-700">PREPAID 반영</span>
                        ) : (
                          <span className="text-amber-700" title="채널 연결 시 자동 생성됩니다. 채널이 이미 연결돼 있는데도 비어 있으면 훅 실패 — 수동 확인 지점.">미반영</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-1 text-xs text-gray-500">
                승인액 = 공급가 + VAT(실제 카드 승인 금액). 수익 대시보드에는 <b>결제월 기준 공급가</b>로 설치비·스티커 시리즈에 포함됩니다.
                환불은 토스 콘솔에서 orderId 로 처리하며 이 화면에는 반영되지 않습니다.
              </p>
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
